import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CouponTopicSection, RichText, SimpleTable } from "@/components/coupons/CouponTopicSection";
import type { CouponBrand } from "@/components/coupons/CouponBrandCard";
import { CATEGORY_GUIDES, CATEGORY_HOW_TO } from "@/content/categoryTopics";
import { buildCouponsGraph, SITE } from "@/lib/couponsSchema";

/** Um cupom por marca: exclusivo > mais usado > mais recente (regra do AGENTS.md). */
async function fetchCategoryCoupons(categoryId: string): Promise<CouponBrand[]> {
  const nowIso = new Date().toISOString();
  const rows: (CouponBrand & { code: string })[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from("coupons")
      .select("id,title,code,discount_type,discount_value,expires_at,uses_count,is_exclusive,updated_at,brand:brands!inner(name,slug,logo_url,category_id,is_active,network_paused,merged_into_id)")
      .eq("type", "code").eq("is_active", true).not("code", "is", null).neq("code", "")
      .eq("brand.is_active", true).eq("brand.network_paused", false).is("brand.merged_into_id", null)
      .eq("brand.category_id", categoryId)
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
      .range(from, from + 999);
    if (error) throw error;
    rows.push(...((data ?? []) as never[]));
    if (!data || data.length < 1000) break;
  }
  const best = new Map<string, CouponBrand>();
  for (const r of rows) {
    const cur = best.get(r.brand.slug);
    const score = (c: CouponBrand) => [c.is_exclusive ? 1 : 0, c.uses_count ?? 0, +new Date(c.updated_at)];
    if (!cur) { best.set(r.brand.slug, r); continue; }
    const a = score(r), b = score(cur);
    if (a[0] > b[0] || (a[0] === b[0] && (a[1] > b[1] || (a[1] === b[1] && a[2] > b[2])))) best.set(r.brand.slug, r);
  }
  return [...best.values()].sort((a, b) => Number(b.is_exclusive) - Number(a.is_exclusive) || (b.uses_count ?? 0) - (a.uses_count ?? 0));
}

export function CategoryCouponGuide({ category }: { category: { id: string; name: string; slug: string } }) {
  const guide = CATEGORY_GUIDES[category.slug];
  const { data: coupons = [] } = useQuery({
    queryKey: ["category-coupons", category.id],
    queryFn: () => fetchCategoryCoupons(category.id),
    enabled: !!guide,
    staleTime: 5 * 60 * 1000,
  });
  if (!guide) return null;

  const url = `${SITE}/marcas/${category.slug}`;
  const brands = coupons.slice(0, 12).map((c) => ({ name: c.brand.name, slug: c.brand.slug }));
  const jsonLd = buildCouponsGraph({
    url,
    name: `Cupons de ${category.name}`,
    description: `Cupons de desconto válidos em lojas de ${category.name}.`,
    breadcrumb: [
      { name: "Início", url: SITE },
      { name: "Lojas", url: `${SITE}/marcas` },
      { name: category.name, url },
    ],
    coupons,
    categoryName: category.name,
    faq: guide.faq,
    steps: CATEGORY_HOW_TO,
    sections: guide.topics.map((t) => ({ id: t.id, title: t.title })),
  });

  return (
    <section className="container mx-auto max-w-4xl space-y-10 px-4 py-10 text-muted-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground md:text-3xl">Guia de cupons de {category.name}</h2>
        <p data-speakable><RichText text={guide.intro} /></p>
        <nav aria-label="Índice" className="rounded-lg border bg-muted/30 p-4">
          <p className="mb-2 font-semibold text-foreground">Neste guia</p>
          <ul className="grid gap-1 sm:grid-cols-2">
            {guide.topics.map((t) => <li key={t.id}><a href={`#${t.id}`} className="text-sm text-link hover:underline">{t.title}</a></li>)}
            <li><a href="#como-usar" className="text-sm text-link hover:underline">Como usar o cupom</a></li>
            <li><a href="#datas" className="text-sm text-link hover:underline">Melhores datas</a></li>
            <li><a href="#perguntas" className="text-sm text-link hover:underline">Perguntas frequentes</a></li>
          </ul>
        </nav>
      </div>

      {guide.topics.map((t) => <CouponTopicSection key={t.id} topic={t} brands={brands} level={3} />)}

      <section id="como-usar" className="scroll-mt-24 space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Como usar um cupom de {category.name}</h2>
        <ol className="list-decimal space-y-1 pl-6">{CATEGORY_HOW_TO.map((s) => <li key={s}>{s}</li>)}</ol>
      </section>

      <section id="datas" className="scroll-mt-24 space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Melhores datas para cupons de {category.name}</h2>
        <SimpleTable head={["Data", "Quando", "O que procurar"]} rows={guide.seasonal} />
      </section>

      <section id="perguntas" className="scroll-mt-24 space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Perguntas frequentes sobre cupons de {category.name}</h2>
        <Accordion type="multiple">
          {guide.faq.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`}>
              <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
              <AccordionContent>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <p>Quer ver outras categorias? Confira todos os <Link to="/cupons-de-desconto" className="text-link underline">cupons de desconto válidos hoje</Link>.</p>
    </section>
  );
}
